import * as changeCase from "change-case"
import {toExchangeParameterName} from "#material/exchange/parameter-converter"

type PinnedCommon = {
    value: number | string
    /** Why nothing else is accepted, as one clause addressed to whoever has to fix the material. */
    reason: string
}

/**
 * A socket or property a Blender newer than 4.2 added to a node the platform otherwise models. Cycles 4.2
 * has exactly one behaviour for each, so the platform takes the value matching that behaviour and refuses
 * everything else, rather than storing a material it would render differently from what the artist approved.
 *
 * The pinned value is what Blender's own versioning writes into a node loaded from a 4.2 file, which is not
 * always the value a newer Blender creates: a fresh bump node carries 0.1, while a 4.2 one becomes 1.0.
 */
export type PinnedParameter =
    | (PinnedCommon & {
          /** A node attribute, which travels under internal., is shown unprefixed, and can never be linked. */
          property: true
          /** Blender shows an attribute as a dropdown, so a message has to name the values the way it spells them. */
          shown: {readonly [value: string]: string}
          output?: never
      })
    | (PinnedCommon & {
          property?: never
          shown?: never
          /** Blender computes this one, so its value never means anything and only a link out of it can. */
          output?: boolean
      })

/** Keyed as the platform declares the setting, the way the recorded gaps name it. */
export const pinnedParameters: {readonly [blenderNodeName: string]: {readonly [settingName: string]: PinnedParameter}} = {
    ShaderNodeBsdfGlass: {
        thinFilmIor: {value: 1.33, reason: "colormass does not render thin film, so its controls have to stay at their defaults"},
        thinFilmThickness: {value: 0, reason: "colormass does not render thin film"},
    },
    ShaderNodeBump: {
        filterWidth: {value: 1.0, reason: "colormass filters bump the way Blender 4.2 did, before the width was settable"},
    },
    ShaderNodeLightPath: {
        isVolumeScatterRay: {value: 0, output: true, reason: "colormass cannot tell whether a ray came from volume scattering"},
        portalDepth: {value: 0, output: true, reason: "colormass does not render ray portals"},
    },
    ShaderNodeNormalMap: {
        base: {
            property: true,
            value: "DISPLACED",
            shown: {DISPLACED: "Displaced", ORIGINAL: "Original"},
            reason: "colormass applies normal maps relative to the displaced surface",
        },
        convention: {
            property: true,
            value: "OPENGL",
            shown: {OPENGL: "OpenGL", DIRECTX: "DirectX"},
            reason: "colormass cannot render DirectX normal maps",
        },
    },
}

/** Marks a parameter the addon sets only if the node has somewhere to put it, so older Blenders skip it silently. */
export const OPTIONAL_PREFIX = "optional."

/** No platform node class governs these names, so a socket takes the plain capitalisation every node shares. */
export const blenderNameFor = (settingName: string, pinned: PinnedParameter): string =>
    pinned.property ? `internal.${changeCase.snakeCase(settingName)}` : toExchangeParameterName(settingName, "", "socket")

/** Never the wire name of a property, because the artist reading this sees an unprefixed field on the node. */
const labelFor = (settingName: string, pinned: PinnedParameter) => (pinned.property ? changeCase.capitalCase(settingName) : blenderNameFor(settingName, pinned))

type ResolvedParameter = {label: string; pinned: PinnedParameter}

const byBlenderName = new Map<string, ResolvedParameter>(
    Object.entries(pinnedParameters).flatMap(([blenderNodeName, parameters]) =>
        Object.entries(parameters).map(
            ([settingName, pinned]) => [`${blenderNodeName}.${blenderNameFor(settingName, pinned)}`, {label: labelFor(settingName, pinned), pinned}] as const,
        ),
    ),
)

const resolvedParameter = (blenderNodeName: string, blenderParameterName: string) => byBlenderName.get(`${blenderNodeName}.${blenderParameterName}`)

/** Values reach us through a float32 socket, so 1.33 arrives as 1.3300000429153442. */
const matchesPinnedValue = (pinned: PinnedParameter, value: unknown) =>
    typeof pinned.value === "number" ? typeof value === "number" && Math.fround(pinned.value) === Math.fround(value) : value === pinned.value

/** A float32 socket turns 0.1 into 0.10000000149011612, which is no use to whoever has to correct the node. */
const spell = (pinned: PinnedParameter, value: unknown) =>
    pinned.shown?.[String(value)] ?? (typeof value === "number" ? String(Number(value.toPrecision(7))) : String(value))

export const isPinnedParameter = (blenderNodeName: string, blenderParameterName: string): boolean =>
    resolvedParameter(blenderNodeName, blenderParameterName) !== undefined

export const pinnedValueError = (blenderNodeName: string, parameter: {name: string; value: unknown}): string | undefined => {
    const resolved = resolvedParameter(blenderNodeName, parameter.name)
    if (!resolved || matchesPinnedValue(resolved.pinned, parameter.value)) return undefined
    const expected = spell(resolved.pinned, resolved.pinned.value)
    return `"${resolved.label}" must be ${expected}, but is ${spell(resolved.pinned, parameter.value)}. ${resolved.pinned.reason}. Set it to ${expected} and upload again.`
}

/** A link decides the value whatever the socket holds, so it has to be refused on the same grounds. */
export const pinnedLinkError = (blenderNodeName: string, socketName: string, side: "source" | "destination"): string | undefined => {
    const resolved = resolvedParameter(blenderNodeName, socketName)
    if (!resolved || (side === "source") !== (resolved.pinned.output === true)) return undefined
    const {label, pinned} = resolved
    if (pinned.output) return `"${label}" cannot be linked. ${pinned.reason}. Remove the link and upload again.`
    return `"${label}" cannot be linked. ${pinned.reason}. Remove the link, set it to ${spell(pinned, pinned.value)} and upload again.`
}

/**
 * Every pinned value travels on every download, so what the platform holds decides the node without the
 * platform having to know what value a given Blender release would create one with.
 */
export const parametersRestoredOnDownload = (blenderNodeName: string): {name: string; type: string; value: number | string}[] =>
    Object.entries(pinnedParameters[blenderNodeName] ?? {}).map(([settingName, pinned]) => ({
        name: `${OPTIONAL_PREFIX}${blenderNameFor(settingName, pinned)}`,
        type: typeof pinned.value === "string" ? "string" : "float",
        value: pinned.value,
    }))
